import {
  availableLoadSchema,
  equipmentSchema,
  gymSchema,
  type AvailableLoad,
  type Equipment,
  type Gym,
} from '@cadencia/contracts';
import { generateAvailableLoads } from '@cadencia/domain';
import { db } from '../db.js';
import { newSyncColumns, putRow, replaceDerivedRows, softDeleteRow, type WriteContext } from '../writes.js';

export async function getDefaultGym(userId: string): Promise<Gym | undefined> {
  const rows = await db.gyms.where('userId').equals(userId).toArray();
  return rows.find((row) => row.deletedAt === null);
}

export async function ensureDefaultGym(ctx: WriteContext, name = 'Mi gimnasio'): Promise<Gym> {
  const existing = await getDefaultGym(ctx.userId);
  if (existing) return existing;
  const gym = gymSchema.parse({ ...newSyncColumns(ctx), name, isDefault: true });
  return putRow(ctx, 'gyms', gym);
}

export async function listEquipment(gymId: string): Promise<Equipment[]> {
  const rows = await db.equipment.where('gymId').equals(gymId).toArray();
  return rows
    .filter((row) => row.deletedAt === null)
    .sort((a, b) => a.label.localeCompare(b.label, 'es'));
}

export async function listAvailableLoads(equipmentId: string): Promise<AvailableLoad[]> {
  const rows = await db.availableLoads.where('equipmentId').equals(equipmentId).toArray();
  return rows.filter((row) => row.deletedAt === null).sort((a, b) => a.loadKg - b.loadKg);
}

export type EquipmentInput = Omit<
  Equipment,
  'id' | 'userId' | 'createdAt' | 'updatedAt' | 'deletedAt' | 'rev' | 'originDeviceId' | 'syncSeq'
>;

/**
 * Guarda una instancia de equipamiento y REGENERA sus cargas seleccionables.
 * Requisito §8.1: una recomendacion nunca puede proponer una carga imposible.
 */
export async function saveEquipment(
  ctx: WriteContext,
  input: EquipmentInput,
  existingId?: string,
  options: { bodyWeightKg?: number } = {},
): Promise<Equipment> {
  const existing = existingId ? await db.equipment.get(existingId) : undefined;
  const candidate = existing
    ? { ...existing, ...input }
    : { ...newSyncColumns(ctx), ...input };

  const equipment = equipmentSchema.parse(candidate);
  const saved = await putRow(ctx, 'equipment', equipment);
  await regenerateAvailableLoads(ctx, saved, options);
  return saved;
}

export async function regenerateAvailableLoads(
  ctx: WriteContext,
  equipment: Equipment,
  options: { bodyWeightKg?: number } = {},
): Promise<AvailableLoad[]> {
  const generated = generateAvailableLoads(
    equipment,
    options.bodyWeightKg === undefined ? {} : { bodyWeightKg: options.bodyWeightKg },
  );

  const rows = generated.map((load) =>
    availableLoadSchema.parse({
      ...newSyncColumns(ctx),
      equipmentId: equipment.id,
      loadKg: load.loadKg,
      source: load.source,
    }),
  );

  await replaceDerivedRows(ctx, 'availableLoads', 'equipmentId', equipment.id, rows);
  return rows;
}

export async function deleteEquipment(ctx: WriteContext, equipmentId: string): Promise<void> {
  await replaceDerivedRows(ctx, 'availableLoads', 'equipmentId', equipmentId, []);
  await softDeleteRow(ctx, 'equipment', equipmentId);
}
